import { itemSorts } from '../../constants/itemSearchConstatnts.js'

const { PRICE_ASC, PRICE_DESC } = itemSorts

/** @typedef {import('../../mappers/itemMapper.js').Item} Item */
/** @typedef {import('../../mappers/itemSkuMapper.js').ItemSku} ItemSku */
/** @typedef {import('./searchConditions.js').SearchConditions} SearchConditions */

/**
 * @typedef {Pick<SearchConditions, 'sortSelectedValue'|'lowerBudget'|'higherBudget'>} ItemBoxConditions
 *
 * @typedef {Object} ItemBox
 * @property {string} id
 * @property {string} itemSkuId
 * @property {string} linkUrl
 * @property {string} imageUrl
 * @property {string} brandName
 * @property {string} itemName
 * @property {string} itemPrice
 * @property {boolean} isIncludingTax
 * @property {boolean} hasMultipleItemSkus
 * @property {boolean} soldOut
 * @property {string[]} labels
 */

/**
 * 予算・販売状態に合う表示SKUの候補を返す。
 * @param {Item} item
 * @param {ItemBoxConditions} conditions
 * @returns {ItemSku[]}
 */
const filterItemSkuCandidates = (item, conditions) =>
  item.itemSkus.filter(
    sku =>
      (item.soldOut || !sku.soldOut) &&
      (conditions.lowerBudget === null ||
        sku.price >= conditions.lowerBudget) &&
      (conditions.higherBudget === null || sku.price <= conditions.higherBudget)
  )

/**
 * 予算・並び替え条件に従って表示SKUを選ぶ。
 * 価格は送料込み。候補は予算の下限・上限を含む範囲で絞り込み済みとする。
 *
 * 選択条件（上から優先）:
 * 1. price_ascは最安価格、price_descは最高価格のSKUを選ぶ。
 * 2. 下限・上限のどちらかでも予算指定があれば、最高価格のSKUを選ぶ。
 * 3. 予算指定がなければ、以下の順で選ぶ。
 *    a. 全SKUが同額なら、SKU IDが最小のもの。
 *    b. 全SKUが5,000円を超えるなら、最安価格のもの。
 *    c. 中央値が5,000円以上なら、5,000円以下で最も高いもの。
 *    d. それ以外は価格順の中央のもの。偶数件では中央2件の安い方。
 * 偶数件の中央値は中央2件の価格の平均で判定する。
 * 選ぶ価格のSKUが複数ある場合は、常にSKU IDが最小のものを選ぶ。
 *
 * 例（特記のないものは予算・並び替え指定なし）:
 * - 500 / 3,000 / 5,000円: 予算なしは3,000円、予算3,000〜5,000円は5,000円。
 *   price_descは5,000円、price_ascは予算なしなら500円、同予算内なら3,000円。
 * - 500 / 3,000 / 4,000 / 4,500円: 中央値は3,500円、表示は中央2件の安い3,000円。
 * - 3,000 / 6,000 / 8,000 / 9,000円: 中央値は7,000円、5,000円以下の最高価格の3,000円。
 * - 3,000 / 5,000 / 8,000円: 中央値が5,000円なので、5,000円を選ぶ。
 * - 6,000 / 8,000 / 10,000円: 全件5,000円超なので、6,000円を選ぶ。
 * - 同額3,000円のSKU IDが9と2: SKU ID 2を選ぶ。
 *
 * @param {ItemSku[]} candidates 予算・販売状態で絞り込み済みの、1件以上のSKU
 * @param {ItemBoxConditions} conditions
 * @returns {ItemSku}
 */
export const selectItemSkuByPrice = (candidates, conditions) => {
  const sorted = [...candidates].sort(
    (first, second) =>
      first.price - second.price || Number(first.id) - Number(second.id)
  )
  const lowestPrice = sorted[0].price
  const highestPrice = sorted[sorted.length - 1].price
  const atPrice = price => sorted.find(sku => sku.price === price)

  if (conditions.sortSelectedValue === PRICE_ASC.key) {
    return atPrice(lowestPrice)
  }
  if (
    conditions.sortSelectedValue === PRICE_DESC.key ||
    conditions.lowerBudget !== null ||
    conditions.higherBudget !== null
  ) {
    return atPrice(highestPrice)
  }

  if (lowestPrice === highestPrice) return atPrice(highestPrice)
  if (lowestPrice > 5000) return atPrice(lowestPrice)

  const lowerMiddle = sorted[Math.floor((sorted.length - 1) / 2)]
  const upperMiddle = sorted[Math.floor(sorted.length / 2)]
  const medianPrice = (lowerMiddle.price + upperMiddle.price) / 2

  if (medianPrice >= 5000) {
    return atPrice(sorted.findLast(sku => sku.price <= 5000).price)
  }
  return atPrice(lowerMiddle.price)
}

/**
 * 表示SKUを選び、商品カードの表示情報を組み立てる。
 * @param {Item} item
 * @param {ItemBoxConditions} conditions
 * @returns {ItemBox|null} 候補がない場合はnull
 */
export const buildSearchItemBox = (item, conditions) => {
  const candidates = filterItemSkuCandidates(item, conditions)
  if (candidates.length === 0) return null
  const selectedSku = selectItemSkuByPrice(candidates, conditions)

  return {
    id: item.id,
    itemSkuId: selectedSku.id,
    linkUrl: `/items/${item.id}/item_skus/${selectedSku.id}`,
    imageUrl: selectedSku.imageUrl,
    brandName: item.brandName,
    itemName: item.name,
    itemPrice: selectedSku.price.toLocaleString(),
    isIncludingTax: selectedSku.isIncludingTax,
    hasMultipleItemSkus: item.hasMultipleItemSkus,
    soldOut: item.soldOut,
    labels: item.labels
  }
}
